import { RUN_TIMED_OUT_ERROR_CODE, type RunEntry, type SetupRun } from '@/types/setup-run'

/** The two stages of a content strategy draft, in order. */
export const CONTENT_STRATEGY_STAGES = ['beliefs', 'core-message'] as const

export type ContentStrategyStage = (typeof CONTENT_STRATEGY_STAGES)[number]

/** The nine belief questions a draft answers, three per journey phase. */
export const BELIEF_QUESTION_KEYS = [
  'awareness.problem',
  'awareness.prevalence',
  'awareness.cost',
  'consideration.options',
  'consideration.our-option',
  'consideration.fit',
  'decision.brand',
  'decision.offer',
  'decision.after-purchase',
] as const

export type BeliefQuestionKey = (typeof BELIEF_QUESTION_KEYS)[number]

/** `errorCode` of a question entry whose call failed or whose belief was refused. */
export const CONTENT_STRATEGY_QUESTION_FAILED = 'content_strategy_question_failed'

/** Progress of one belief question, id `question:{questionKey}`. */
export interface ContentStrategyQuestionEntry extends RunEntry {
  readonly kind: 'question'
  readonly questionKey: BeliefQuestionKey
  readonly errorCode?: typeof CONTENT_STRATEGY_QUESTION_FAILED
}

/** One belief a draft wrote for one question. */
export interface DraftedBelief {
  readonly name: string
  readonly claim: string
  readonly objection: string
}

/** The belief of one question, or `null` when its call failed. */
export interface BeliefRow {
  readonly questionKey: BeliefQuestionKey
  readonly belief: DraftedBelief | null
}

export const CONTENT_STRATEGY_ERROR_CODES = [
  'content_strategy_enqueue_failed',
  'content_strategy_failed',
  'content_strategy_customer_profile_missing',
  'content_strategy_facts_missing',
  RUN_TIMED_OUT_ERROR_CODE,
] as const

export type ContentStrategyErrorCode = (typeof CONTENT_STRATEGY_ERROR_CODES)[number]

export const CONTENT_STRATEGY_RUN_STALE_AFTER_MS = 5 * 60 * 1000

/** One content strategy draft, `contentStrategyRuns/{runId}`. */
export interface ContentStrategyRun extends SetupRun<
  ContentStrategyStage,
  ContentStrategyQuestionEntry
> {
  readonly errorCode?: ContentStrategyErrorCode
  readonly keepEdited: boolean
  readonly startedByUserId: string
  /** The beliefs written so far, in question order. */
  readonly beliefs: readonly BeliefRow[]
}

export const CONTENT_STRATEGY_REFUSAL_REASONS = [
  'content_strategy_run_in_progress',
  'content_strategy_customer_profile_missing',
  'belief_map_missing',
  'obvious_choice_belief_missing',
  'content_strategy_missing',
  'belief_map_replaces_too_long',
  'owner_admin_role_required',
  'editor_role_required',
] as const

export type ContentStrategyRefusalReason = (typeof CONTENT_STRATEGY_REFUSAL_REASONS)[number]

/** How many question entries are done. */
export function answeredQuestionCount(run: {
  readonly entries: readonly ContentStrategyQuestionEntry[]
}): number {
  return run.entries.filter((entry) => entry.step === 'done').length
}
